import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { hasCredentials, clearCredentials, getMe } from './api/client'
import { ApiError } from './api/core'
import { verifyEmailToken } from './api/users'
import { useStore, pathToView } from './store'
import { LoginForm } from './components/LoginForm'
import { AppHeader } from './components/AppHeader'
import { AppViews } from './components/AppViews'
import { useDarkMode } from './hooks/useDarkMode'
import i18n from './i18n'

export default function App() {
  const { theme, toggleTheme } = useDarkMode()
  const [loggedIn, setLoggedIn] = useState(hasCredentials)
  const { setCurrentUser, setCurrentSession, setError, setActiveView, setLanguage } = useStore()
  const [meRetryCount, setMeRetryCount] = useState(0)
  const [verifyBanner, setVerifyBanner] = useState<{ ok: boolean; msg: string } | null>(null)

  // E-mailadres verificatielink afhandelen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('verify_token')
    if (!token) return
    // Verwijder token uit URL zonder herladen
    const clean = window.location.pathname
    window.history.replaceState({}, '', clean)
    verifyEmailToken(token)
      .then(res => {
        if (res.status === 'verified') {
          setVerifyBanner({ ok: true, msg: i18n.t('email_verify.success') })
        } else {
          setVerifyBanner({ ok: true, msg: i18n.t('email_verify.already_verified') })
        }
      })
      .catch(() => setVerifyBanner({ ok: false, msg: i18n.t('email_verify.error') }))
  }, [])

  useEffect(() => {
    if (!loggedIn) return
    getMe()
      .then(user => {
        setCurrentUser(user)
        const lang = user.preferred_language ?? 'nl'
        setLanguage(lang)
        i18n.changeLanguage(lang)
        Sentry.setUser({ id: String(user.id), username: user.username })
      })
      .catch(err => {
        // Alleen een echte 401 betekent dat de sessie ongeldig is — een andere
        // fout (bv. een transiënte 500 door tijdelijke serverdrukte, zoals bij
        // meerdere snelle paginaverversingen kort na elkaar) mag de gebruiker
        // niet uitloggen. Eén keer automatisch opnieuw proberen na een korte
        // pauze i.p.v. meteen de token weg te gooien.
        if (err instanceof ApiError && err.status === 401) {
          clearCredentials()
          setLoggedIn(false)
          return
        }
        Sentry.captureException(err)
        if (meRetryCount < 1) {
          setTimeout(() => setMeRetryCount(c => c + 1), 800)
        } else {
          setError(i18n.t('app_views.load_user_failed'))
        }
      })
  }, [loggedIn, meRetryCount])

  useEffect(() => {
    const onPop = () => useStore.setState({ activeView: pathToView(window.location.pathname) })
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  if (!loggedIn) return (
    <>
      {verifyBanner && (
        <div className={`flex items-center justify-between px-4 py-2 text-sm font-medium ${verifyBanner.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <span>{verifyBanner.msg}</span>
          <button onClick={() => setVerifyBanner(null)} className="ml-4 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}
      <LoginForm onLogin={() => { setMeRetryCount(0); setLoggedIn(true) }} />
    </>
  )

  const handleLogout = () => {
    clearCredentials()
    setCurrentUser(null)
    Sentry.setUser(null)
    setLoggedIn(false)
  }

  const handleNewUpload = () => {
    setCurrentSession(null)
    setError(null)
    setActiveView('upload')
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F0F7FB] dark:bg-gray-950 transition-colors duration-200">
      {verifyBanner && (
        <div className={`flex items-center justify-between px-4 py-2 text-sm font-medium ${verifyBanner.ok ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-b border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-b border-red-200 dark:border-red-800'}`}>
          <span>{verifyBanner.msg}</span>
          <button onClick={() => setVerifyBanner(null)} className="ml-4 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}
      <AppHeader theme={theme} toggleTheme={toggleTheme} onLogout={handleLogout} />
      <AppViews onNewUpload={handleNewUpload} />
    </div>
  )
}
